import { createHash, randomUUID } from 'node:crypto';
import { getDatabase } from '../cache/db.js';
import { createLogger } from '../logger.js';
import type { WatchJob, WatchJobStatus } from '../types.js';

const log = createLogger('cache');

interface WatchJobRow {
  id: string;
  url: string;
  interval_seconds: number;
  selector: string | null;
  last_check_at: number | null;
  last_content_hash: string | null;
  status: WatchJobStatus;
  notification: string;
  created_at: number;
}

function rowToJob(row: WatchJobRow, now: number): WatchJob {
  const job: WatchJob = {
    id: row.id,
    url: row.url,
    interval_seconds: row.interval_seconds,
    ...(row.selector ? { selector: row.selector } : {}),
    ...(row.last_check_at !== null ? { last_check_at: row.last_check_at } : {}),
    ...(row.last_content_hash !== null ? { last_content_hash: row.last_content_hash } : {}),
    status: row.status,
    notification: row.notification,
    created_at: row.created_at,
  };
  // staleness_seconds: how overdue the job is, computed at read time.
  // - Positive: how many seconds the job is overdue.
  // - Negative: how many seconds until the next check is due.
  // - When last_check_at is null (never run), use created_at so the freshly
  //   created job's staleness starts at 0 and grows.
  const ref = row.last_check_at ?? row.created_at;
  const dueAt = ref + row.interval_seconds * 1000;
  job.staleness_seconds = Math.floor((now - dueAt) / 1000);
  return job;
}

/**
 * Deterministic fingerprint used for idempotent `create` — same url +
 * interval + selector returns the existing job_id rather than inserting a
 * duplicate. Selector NULL collapses to empty string so a "no selector" job
 * compares equal to itself.
 */
function fingerprint(url: string, intervalSeconds: number, selector: string | undefined): string {
  return createHash('sha256')
    .update(`${url} ${intervalSeconds} ${selector ?? ''}`)
    .digest('hex');
}

export interface CreateJobInput {
  url: string;
  intervalSeconds: number;
  selector?: string;
  notification: string;
}

export function createJob(input: CreateJobInput): WatchJob {
  const db = getDatabase();
  const fp = fingerprint(input.url, input.intervalSeconds, input.selector);

  // Idempotency lookup. We hash on the same triple every time so a repeat
  // create returns the existing job_id without a new row.
  const existing = db
    .prepare(
      `SELECT id, url, interval_seconds, selector, last_check_at, last_content_hash, status, notification, created_at
       FROM watch_jobs WHERE id = ?`,
    )
    .get(fp) as WatchJobRow | undefined;

  const now = Date.now();
  if (existing) {
    return rowToJob(existing, now);
  }

  const row: WatchJobRow = {
    id: fp,
    url: input.url,
    interval_seconds: input.intervalSeconds,
    selector: input.selector ?? null,
    last_check_at: null,
    last_content_hash: null,
    status: 'active',
    notification: input.notification,
    created_at: now,
  };

  db.prepare(
    `INSERT INTO watch_jobs (id, url, interval_seconds, selector, last_check_at, last_content_hash, status, notification, created_at)
     VALUES (@id, @url, @interval_seconds, @selector, @last_check_at, @last_content_hash, @status, @notification, @created_at)`,
  ).run(row);

  log.info('watch job created', { id: row.id, url: row.url, interval: row.interval_seconds });
  return rowToJob(row, now);
}

export function listJobs(): WatchJob[] {
  const db = getDatabase();
  const rows = db
    .prepare(
      `SELECT id, url, interval_seconds, selector, last_check_at, last_content_hash, status, notification, created_at
       FROM watch_jobs ORDER BY created_at ASC`,
    )
    .all() as WatchJobRow[];
  const now = Date.now();
  return rows.map((r) => rowToJob(r, now));
}

export function getJob(id: string): WatchJob | null {
  const db = getDatabase();
  const row = db
    .prepare(
      `SELECT id, url, interval_seconds, selector, last_check_at, last_content_hash, status, notification, created_at
       FROM watch_jobs WHERE id = ?`,
    )
    .get(id) as WatchJobRow | undefined;
  if (!row) return null;
  return rowToJob(row, Date.now());
}

export function deleteJob(id: string): boolean {
  const db = getDatabase();
  const r = db.prepare('DELETE FROM watch_jobs WHERE id = ?').run(id);
  if (r.changes > 0) log.info('watch job deleted', { id });
  return r.changes > 0;
}

export function setJobStatus(id: string, status: WatchJobStatus): WatchJob | null {
  const db = getDatabase();
  const r = db.prepare('UPDATE watch_jobs SET status = ? WHERE id = ?').run(status, id);
  if (r.changes === 0) return null;
  return getJob(id);
}

export interface CheckResult {
  job: WatchJob;
  changed: boolean;
  previousHash?: string;
  currentHash: string;
  diffSummary?: string;
}

export function recordCheck(
  id: string,
  checkedAt: number,
  contentHash: string,
): WatchJob | null {
  const db = getDatabase();
  const r = db
    .prepare(
      `UPDATE watch_jobs
         SET last_check_at = ?, last_content_hash = ?
       WHERE id = ?`,
    )
    .run(checkedAt, contentHash, id);
  if (r.changes === 0) return null;
  return getJob(id);
}

/**
 * Jobs whose next check is due. A job is due when:
 *   - status is 'active'
 *   - last_check_at + interval_seconds*1000 <= now, OR
 *   - last_check_at is NULL and created_at + interval_seconds*1000 <= now.
 *
 * Used by the lazy fire-and-forget scheduler hook. Returning all overdue
 * jobs lets the caller decide how aggressively to fan out.
 */
export function getOverdueJobs(now: number = Date.now()): WatchJob[] {
  const db = getDatabase();
  const rows = db
    .prepare(
      `SELECT id, url, interval_seconds, selector, last_check_at, last_content_hash, status, notification, created_at
         FROM watch_jobs
        WHERE status = 'active'
          AND (
            (last_check_at IS NULL AND created_at + (interval_seconds * 1000) <= ?)
            OR (last_check_at IS NOT NULL AND last_check_at + (interval_seconds * 1000) <= ?)
          )
        ORDER BY created_at ASC`,
    )
    .all(now, now) as WatchJobRow[];
  return rows.map((r) => rowToJob(r, now));
}

/**
 * Test-only helper — generates a stable but caller-distinguishable id
 * when a code path needs a job id outside the idempotent-create flow.
 * Production code uses `createJob()` which derives the id deterministically.
 */
export function _generateAdHocId(): string {
  return randomUUID();
}
